import { Button, Skeleton } from '@bennows/boows-react';
import { CalendarDays, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMonthYear } from '../../lib/format';
import type { ResolvedContext } from './assistant-api';

interface Props {
  readonly loading: boolean;
  /** The server's resolved context, or null when it is not read (yet), failed or the member removed it. */
  readonly resolved: ResolvedContext | null;
  readonly removable: boolean;
  readonly onRemove: () => void;
}

/** The label of a resolved context: the server names the key and the IDs' titles, the month is formatted here. */
export function contextLabel(t: (key: string, params?: Record<string, unknown>) => string, resolved: ResolvedContext | null): string {
  if (!resolved) return t('assistant.context.none');
  const params = { ...resolved.label_params } as Record<string, unknown>;
  if (typeof params.month === 'string' && /^\d{4}-\d{2}$/.test(params.month)) params.month = formatMonthYear(`${params.month}-01`);
  const key = resolved.kind === 'calendar' && params.month === undefined ? 'assistant.context.calendarNoMonth' : resolved.label_key;
  return t(key, params);
}

/** "Bezieht sich auf: ..." (spec 110 section 4.15.6): the page the question is about, removable with one tap. */
export function ContextLine({ loading, resolved, removable, onRemove }: Props) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="ui-assistant__context">
        <Skeleton variant="line" />
        <span className="ui-visually-hidden">{t('assistant.contextLoading')}</span>
      </div>
    );
  }
  return (
    <div className="ui-assistant__context">
      <CalendarDays aria-hidden="true" />
      <span className="ui-assistant__context-label">{t('assistant.contextLine', { label: contextLabel(t, resolved) })}</span>
      {removable && resolved && resolved.kind !== 'none' ? (
        <Button variant="ghost" size="sm" iconOnly aria-label={t('assistant.contextRemove')} onClick={onRemove}>
          <X aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}
